import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { PricingCard } from "../components/PricingCard";
import { BookOpen, PenTool, FileText, Sparkles, Brain, Wand2, Users, ArrowRight, Shield, CreditCard, Coins, BarChart, Database, Smartphone } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../hooks/use-auth";
import { getVisiblePlans } from "../../../shared/plans";

export default function LandingPage() {
  const { user } = useAuth();

  const { data: plansData, isLoading, error } = useQuery({
    queryKey: ["/api/billing/plans"],
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Use billing plans data, fallback to static plans if loading or error
  const plans = Array.isArray(plansData) ? plansData : getVisiblePlans();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-xl shadow-2xl border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4 sm:space-x-8">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                  <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <h1 className="text-xl sm:text-2xl font-bold text-white">StoryLikho</h1>
              </div>
              <nav className="hidden md:flex space-x-6 lg:space-x-8">
                <a href="#features" className="text-white/80 hover:text-white transition-colors font-medium">Features</a>
                <a href="#pricing" className="text-white/80 hover:text-white transition-colors font-medium">Pricing</a>
                <a href="#how-it-works" className="text-white/80 hover:text-white transition-colors font-medium">How It Works</a>
              </nav>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              {user ? (
                <Link to="/my-stories">
                  <Button className="bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 text-sm sm:text-base h-9 sm:h-10">
                    My Stories
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/auth">
                    <Button variant="ghost" className="text-white hover:bg-white/20 transition-all duration-300 text-sm sm:text-base h-9 sm:h-10">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/auth?mode=register">
                    <Button className="bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 text-sm sm:text-base h-9 sm:h-10">
                      Start Writing Free
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-100 to-blue-100 py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className="p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl shadow-lg">
                <Sparkles className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-purple-600" />
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-4 sm:mb-6 leading-tight">
              AI-Powered Story Writing Studio
            </h1>
            <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
              Transform your ideas into compelling stories with AI assistance. Create characters, build worlds, and craft narratives that captivate readers.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              {user ? (
                <Link to="/my-stories" className="w-full sm:w-auto">
                  <Button size="lg" className="text-base sm:text-lg px-6 sm:px-8 py-3 bg-purple-600 hover:bg-purple-700 w-full sm:w-auto h-12 sm:h-auto">
                    <BookOpen className="mr-2 h-5 w-5" />
                    Go to My Stories
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/auth?mode=register" className="w-full sm:w-auto">
                    <Button size="lg" className="text-base sm:text-lg px-6 sm:px-8 py-3 bg-purple-600 hover:bg-purple-700 w-full sm:w-auto h-12 sm:h-auto">
                      <PenTool className="mr-2 h-5 w-5" />
                      Start Writing Free
                    </Button>
                  </Link>
                  <Link to="/templates" className="w-full sm:w-auto">
                    <Button size="lg" variant="outline" className="text-base sm:text-lg px-6 sm:px-8 py-3 border-purple-200 hover:bg-purple-50 w-full sm:w-auto h-12 sm:h-auto">
                      <ArrowRight className="mr-2 h-5 w-5" />
                      View Demo
                    </Button>
                  </Link>
                </>
              )}
            </div>
            <div className="mt-8 text-sm text-purple-600 font-medium">
              <p>✨ 100 free credits to get started • No credit card required</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Everything You Need to Write Great Stories</h2>
            <p className="text-xl text-slate-600">Professional writing tools powered by AI to help you craft compelling narratives</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card>
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center mb-4">
                  <Sparkles className="text-white h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold mb-2">AI Story Generation</h3>
                <p className="text-slate-600">Create chapters, character development, and plot outlines with advanced AI assistance tailored to your genre.</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mb-4">
                  <CreditCard className="text-white text-xl" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Payment Integration</h3>
                <p className="text-slate-600">Seamless Razorpay integration for secure payment processing and plan management.</p> {/* Updated text */}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center mb-4">
                  <Coins className="text-white text-xl" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Credit System</h3>
                <p className="text-slate-600">Flexible credit-based system for purchases, earnings, and task completion tracking.</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mb-4">
                  <BarChart className="text-white text-xl" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Analytics Dashboard</h3>
                <p className="text-slate-600">Comprehensive analytics and user management with credit history and insights.</p> {/* Updated text */}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center mb-4">
                  <Database className="text-white text-xl" />
                </div>
                <h3 className="text-xl font-semibold mb-2">PostgreSQL Backend</h3>
                <p className="text-slate-600">Robust database architecture with secure session management and API endpoints.</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mb-4">
                  <Smartphone className="text-white text-xl" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Mobile Responsive</h3>
                <p className="text-slate-600">Fully responsive design optimized for all devices with smooth transitions and loading states.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Choose Your Plan</h2>
            <p className="text-xl text-slate-600">Flexible pricing to fit your needs</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {plans.map((plan: any, index: number) => (
              <PricingCard
                key={plan.code} // Use plan.code as key
                plan={{ ...plan, isPopular: plan.code === "PRO" }}
                isCurrentPlan={false} // Assuming no plan is current on landing page
                onSelectPlan={() => {}} // Placeholder, as there's no direct plan selection on landing
              />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="col-span-1">
              <h3 className="text-2xl font-bold mb-4">StoryLikho</h3>
              <p className="text-slate-300 mb-4">AI-powered creative writing platform for authors, storytellers, and content creators.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-slate-300">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API Reference</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-slate-300">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-slate-300">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Community</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-700 mt-12 pt-8 text-center text-slate-400">
            <p>&copy; 2025 StoryLikho. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
